import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { LoadingEmptyData } from "go-core/components/Loading";
import { ReactComponent as SearchSVG } from "go-core/images/svg/search.svg";
import { FormDirty } from "go-form/components/FormDirty";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../../services/Api/Admin/adminApi";
import { MergeOrganizationApi } from "../../../../../../services/Api/Admin/adminTypes";
import { OrganizationApi } from "../../../../../../services/Api/types";
import { OrganizationSearchOption } from "../../../Loan/CreateLoanModal";

interface Props {
	onHide: () => void;
	isShown: boolean;
	organization: OrganizationApi;
	fetchOrganization: () => Promise<void | OrganizationApi>;
}

const OrganizationMergingModal = ({ onHide, isShown, organization, fetchOrganization }: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<MergeOrganizationApi>({
		defaultValues: {},
	});
	const [searchValue, setSearchValue] = useState<string>("");
	const [searchLoading, setSearchLoading] = useState<boolean>(false);
	const [organizations, setOrganizations] = useState<OrganizationSearchOption[]>([]);
	const chosenOrganizationName = useRef("");
	const { setValue, handleSubmit, formState } = form;
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();

	useEffect(() => {
		onSearchOrganizationHandler();
	}, [searchValue]);

	const onSearchOrganizationHandler = async () => {
		setSearchLoading(true);
		const obj = {
			company_tax_id_no: organization.company_tax_id_no,
		};
		const res = await adminApi.getListAdminOrganizationsSearchSelect(searchValue, obj);
		setOrganizations([
			...res
				.filter((x) => Number(x.id) !== Number(organization.id))
				.map((data) => {
					return {
						name: data.label,
						id: Number(data.id),
					};
				}),
		]);
		setSearchLoading(false);
	};

	const onAddOrganizationOptionHandler = async (item: OrganizationSearchOption) => {
		setValue("organization_id", item.id);
		chosenOrganizationName.current = item.name;
		onHide();
		await onSubmit();
		await fetchOrganization();
	};

	const onSubmit = handleSubmit(async (data) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("modules.organization.field.confirmation_merging.title", {
					organization: chosenOrganizationName.current,
				}),
			});
			await adminApi.mergeOrganization(organization.id, data);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	});

	return (
		<Modal show={isShown} onHide={onHide}>
			<FormDirty formState={formState} key="organization-merging-form" onSubmit={onSubmit}>
				<Modal.Header closeButton>
					{/*<Modal.Title>{t("components.organization_merging_modal.header.title")}</Modal.Title>*/}
					<Modal.Title>{t("modules.organization.field.choose_organization.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="items">
					<div className="sticky-modal-search-header">
						<div className="form-group search-input">
							<div className="input-group">
								<input
									type="text"
									className="form-control"
									value={searchValue}
									placeholder={t("common.word.search", { ns: "lib" })}
									style={{ borderRadius: "6px" }}
									onChange={(evt) => setSearchValue(evt.target.value)}
								/>
								<SearchSVG className="search-icon" />
							</div>
						</div>
					</div>
					<table className="table">
						<tbody>
							{searchLoading ? (
								<LoadingEmptyData />
							) : (
								<>
									{organizations.length > 0 ? (
										organizations.map((x, index) => (
											<tr key={index}>
												<td>
													<Link
														to={`/admin/organizations/${x.id}`}
														target={"_blank"}
														rel="noreferrer"
													>
														{x.name}
													</Link>
												</td>
												<td className="px-0">
													<Button
														className="float-end"
														variant={"primary-light"}
														type="button"
														onClick={() => onAddOrganizationOptionHandler(x)}
													>
														{t("common.action.select", { ns: "lib" })}
													</Button>
												</td>
											</tr>
										))
									) : (
										<EmptyData />
									)}
								</>
							)}
						</tbody>
					</table>
				</Modal.Body>
			</FormDirty>
		</Modal>
	);
};
export default OrganizationMergingModal;
